<template>
  <section class="view-rich-block">
    <component
      :is="componentFor(blockId)"
      v-if="componentFor(blockId)"
      :key="blockId"
      v-bind="componentProps"
    />
    <div v-else class="view-rich-fallback">
      <p>This interactive element is currently unavailable.</p>
      <router-link :to="fallbackRoute">Open the original project</router-link>
    </div>
  </section>
</template>

<script>
import EmbeddingMap from '../EmbeddingMap.vue';
import RetrievalLab from './RetrievalLab.vue';
import InsuranceRuleEngine from './InsuranceRuleEngine.vue';
import ReferenceLinkingDemo from './ReferenceLinkingDemo.vue';
import FlowchartReconstructionDemo from './FlowchartReconstructionDemo.vue';
import ChartExplorer from './ChartExplorer.vue';
import LiveDeckEmbed from './LiveDeckEmbed.vue';
import JourneyTab from '../JourneyTab.vue';
import AboutAiEntry from '../AboutAiEntry.vue';
import SiteAiPathExplorer from './SiteAiPathExplorer.vue';
import ViewComposeInvitation from './ViewComposeInvitation.vue';
import ItOpsJourney from './ItOpsJourney.vue';
import DocumentReviewArchitecture from '../document-review/DocumentReviewArchitecture.vue';
import RiskAssessmentGuide from '../document-review/RiskAssessmentGuide.vue';
import ArchiveProjectEvidence from './ArchiveProjectEvidence.vue';
import SlidePoiseFilm from '../ui/SlidePoiseFilm.vue';
import CathayFlexFilm from '../ui/CathayFlexFilm.vue';
import { richBlockById } from '../../data/viewRichBlocks';

const COMPONENTS = {
  EmbeddingMap,
  RetrievalLab,
  InsuranceRuleEngine,
  ReferenceLinkingDemo,
  FlowchartReconstructionDemo,
  ChartExplorer,
  LiveDeckEmbed,
  TravelGlobe: JourneyTab,
  AboutAiEntry,
  SiteAiPathExplorer,
  ViewComposeInvitation,
  ItOpsJourney,
  DocumentReviewArchitecture,
  RiskAssessmentGuide,
  ArchiveProjectEvidence,
  SlidePoiseFilm,
  CathayFlexFilm,
};

export default {
  name: 'ViewRichBlock',
  components: COMPONENTS,
  props: {
    blockId: {
      type: String,
      required: true,
    },
    source: {
      type: Object,
      default: null,
    },
  },
  computed: {
    registryEntry() {
      return richBlockById(this.blockId) || null;
    },
    fallbackRoute() {
      return this.registryEntry ? this.registryEntry.route : '/projects';
    },
    componentProps() {
      if (this.registryEntry?.component === 'ArchiveProjectEvidence') {
        const entity = this.source?.entity || this.registryEntry.entity;
        return { detail: entity.detail, scene: entity.scene || '' };
      }
      if (this.registryEntry?.component === 'ItOpsJourney') {
        return { kind: this.blockId === 'interactive.it-ops.browser' ? 'browser' : 'knowledge' };
      }
      if (this.registryEntry?.component !== 'TravelGlobe') return {};
      const places = Array.isArray(this.source?.entity?.places)
        ? this.source.entity.places
        : (this.registryEntry.entity?.places || []);
      const mediaByAlt = new Map((this.source?.media || []).map((item) => [item.alt, item]));
      return {
        embedded: true,
        placesOverride: places.map((place) => {
          const media = mediaByAlt.get(place.en);
          return {
            ...place,
            preview: media?.src || place.preview,
            img: media?.fullSrc || media?.src || place.img,
          };
        }),
        initialFocus: this.source?.entity?.focus_place?.en
          || this.registryEntry.entity?.focus_place?.en
          || '',
      };
    },
  },
  methods: {
    componentFor(id) {
      const entry = richBlockById(id) || this.registryEntry;
      return entry ? COMPONENTS[entry.component] : null;
    },
  },
};
</script>

<style scoped>
.view-rich-block {
  margin: 1.5rem 0;
}

.view-rich-fallback {
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  color: #777;
}

.view-rich-fallback p {
  margin: 0 0 0.45rem;
}

body.dark-mode .view-rich-fallback {
  border-color: rgba(255, 255, 255, 0.14);
  color: #9a9a9a;
}
</style>
